/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — screen 2, Template Editor (R2)
 * Purpose      : Provider authoring surface. Token picker + immediate save; a provider
 *                edit goes live with no approval gate.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * PR 7910 T58    | 04-Aug-2026 | Vijayakumar S  | canSave now blocks on unknown tokens —
 *                                                 nothing server-side validates them here
 * PR 7910 T57    | 04-Aug-2026 | Vijayakumar S  | Revision path made honest against the
 *                                                 fixed backend: revisionOf held in state so
 *                                                 "Author another" cannot silently mint
 *                                                 another version; channel locked while
 *                                                 revising (proposeChange ignores it)
 * Comms draft/    | 05-Aug-2026 | Claude         | A brand-new template now saves as a DRAFT —
 * publish                                         not live until published from the template
 *                                                 list (POST /{uid}/publish). A REVISION of an
 *                                                 existing template is unchanged: still goes
 *                                                 live immediately on save. See
 *                                                 createTemplateAsProvider's own doc comment for
 *                                                 why (the two cases genuinely differ now).
 * Comms edit-in-  | 05-Aug-2026 | Claude         | Adds a third mode, EDIT: the Template List's
 * place                                           new "Edit" row action hands this screen a seed
 *                                                 with `editInPlace: true`, and saving calls
 *                                                 editTemplate (PUT /{uid}/edit) instead of
 *                                                 createTemplateAsProvider — same version, no new
 *                                                 row, status/live-state unchanged. Closes the
 *                                                 "cannot LOAD an existing template to edit" gap
 *                                                 this file used to document.
 * Comms defer     | 06-Aug-2026 | Claude         | SUPERSEDED same day by "Comms remove
 * validation                                       required-field messaging" below — deferred the
 *                                                 three required-field messages behind a `touched`
 *                                                 flag instead of showing them on a pristine form.
 *                                                 Kept here only as the immediate predecessor of
 *                                                 that later, fuller removal.
 * Comms channel   | 06-Aug-2026 | Claude         | New `channelLocked` prop: the Template List's
 * from tab                                         "New template" button is only ever reached
 *                                                 from the Email or SMS tab, so it now seeds
 *                                                 `initial.channel` from that tab AND sets this,
 *                                                 locking the picker — there is nothing left to
 *                                                 choose. Also drops the brand-new-template
 *                                                 branch of the "no approval gate" warning banner
 *                                                 entirely, at the user's request.
 * Comms edit-in-  | 06-Aug-2026 | Claude         | Dropped the edit-in-place branch of the "no
 * place banner                                     approval gate" warning banner too — same
 *                                                 pattern as the brand-new-template branch above,
 *                                                 same user request. Only the revision branch is
 *                                                 left; it is the only mode where saving still has
 *                                                 a real, non-obvious consequence to state.
 * Comms remove    | 06-Aug-2026 | Claude         | Reverses part of "Comms defer validation"
 * required-field                                   above: the three required-field messages
 * messaging                                        (`error`/"Required." text) are gone entirely,
 *                                                 not merely deferred — `touched` no longer has
 *                                                 any reader and is removed. A disabled Save
 *                                                 button is considered sufficient signal on its
 *                                                 own. The unknown-token block (T58) is untouched:
 *                                                 a different class of problem (actively wrong
 *                                                 content, not merely incomplete), still shown
 *                                                 unconditionally.
 * Comms drop     | 06-Aug-2026 | Claude         | Third and final step in the same-day banner
 * remaining                                        removal above: the two banners still shown on
 * revision banners                                 a revision ("Saving publishes immediately —
 *                                                 there is no approval step", "This saves a new
 *                                                 VERSION — it does not create a second template")
 *                                                 are gone too, at the user's request. Nothing
 *                                                 renders here in any of the three modes now. The
 *                                                 Save button's own label is what is left to state
 *                                                 a revision's consequence.
 * 30639           | 14-Sep-2026 | Shabnam        | Added business-flow template categories and
 *                                                  channel/category default selection for
 *                                                  Estimate Link templates.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import HtmlPreview from './HtmlPreview';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import {
  createTemplateAsProvider, editTemplate, fetchDefaultHolder, fetchTemplateCategories, setTemplateCategory,
} from './MessageTemplateService';
import {
  CHANNEL_EMAIL, CHANNEL_SMS, TemplateCategoryOption, TemplateSummary, channelLabel, statusLabel,
} from './CommunicationTypes';
import { TokenPicker } from './TokenPicker';
import { extractTokens } from './templateDiff';
import { isKnownToken } from './CommunicationTokenCatalog';

/**
 * R2 — A PROVIDER EDIT IS NEVER SENT TO SOMEONE ELSE FOR REVIEW.
 *
 * There is no human review step for provider-authored content, ever — that part of R2 is
 * unchanged. What DOES depend on which case this is (2026-08-05 draft/publish work) is
 * whether saving goes live immediately:
 *
 * TWO SERVER PATHS BEHIND ONE POST. `MessageTemplateApi.createAsProvider` branches on
 * `templateMasterUid`:
 *
 *   absent  -> createDraft only                — a brand-new template, v1, saved as a
 *              DRAFT. NOT live. A provider explicitly publishes it later (POST
 *              /{uid}/publish, from the template list) when ready — still no separate
 *              reviewer, just an explicit pause between authoring and going live. Before
 *              2026-08-05 this also auto-approved and claimed to be "live for every
 *              tenant" — which, since PR-20 re-keyed liveness onto PUBLISHED, was no
 *              longer true (approve() alone does not publish); every template created
 *              this way sat invisible to every tenant despite the UI's claim otherwise.
 *   present -> proposeChange -> approve -> publish  — the NEXT VERSION of that existing
 *              family, live for every tenant immediately, same as before. `proposeChange`
 *              locks every row for the uid and takes MAX(version) + 1, so the version
 *              number is the SERVER's and cannot be predicted here; read it off the
 *              response, never compute it.
 *
 * WHAT `proposeChange` DOES NOT ACCEPT — and why the channel control locks while revising.
 * Its signature is (uid, name, subject, body, submittedByTenant, submittedByUser): there is
 * NO channel parameter. The next version inherits `live.getChannel()` unconditionally. So a
 * channel change made while revising is silently discarded, and worse in both directions —
 * Email -> SMS strips the subject here, which `proposeChange` then re-inherits from the live
 * version, leaving a row that kept BOTH its old channel and its old subject while the author
 * believes it was converted; SMS -> Email sends a subject the server rejects with an opaque
 * 400. Disabling the control is the honest surface for a field the server will not honour.
 *
 * CHANNEL / SUBJECT COUPLING is a database constraint, not a style preference. The DB
 * CHECK `chk_template_subject_by_channel` forbids a subject on an SMS row, so the subject
 * field is hidden AND stripped from the payload when SMS is selected. Leaving an empty
 * string in would trip the constraint and surface as an opaque 400.
 *
 * UNKNOWN TOKENS ARE BLOCKED HERE BECAUSE NOTHING ELSE BLOCKS THEM (T58). The tenant side
 * validates server-side — `SmsTemplateService.validateUpsert` runs
 * `CommunicationTokenValidator.unknownTokens` and 400s. The provider side has no equivalent:
 * neither `createDraft` nor `proposeChange` looks at tokens at all. This gate is therefore
 * the ONLY thing between a typo'd `{{token}}` and a body that is live for every tenant and
 * renders blank at send time. It is not a convenience check and must not be relaxed into a
 * warning; the right way to remove it is to add the validator to the provider service first.
 *
 * ---------------------------- SCOPE LIMIT, updated 2026-08-05 ----------------------------
 * This editor now has THREE modes, distinguished by `isEditInPlace`/`isRevision` (both derived,
 * never both true at once):
 *
 *   NEW      — no seed, or a seed with no templateMasterUid. createTemplateAsProvider ->
 *              createDraft only. See the R2 note above.
 *   REVISION — a seed's templateMasterUid, WITHOUT `editInPlace`. createTemplateAsProvider's
 *              proposeChange -> approve -> publish path — a new version, live immediately.
 *              Reached from the Template List's "Edit" action is NOT how this mode is entered
 *              (that always sets `editInPlace: true` — see EDIT below); nothing in this screen
 *              currently routes here other than a caller handing `initial` a templateMasterUid
 *              directly (e.g. tests), which remains correct and supported.
 *   EDIT     — a seed's templateMasterUid, WITH `editInPlace: true` (router state), set by the
 *              Template List's "Edit" row action on a DRAFT/APPROVED/REJECTED/CHANGES_REQUESTED,
 *              provider-owned row. Calls `editTemplate` (PUT /{uid}/edit) — same version, no new
 *              row, status/live-state unchanged. This is what makes the previously-documented gap
 *              ("cannot LOAD an existing template to edit") no longer true.
 * -----------------------------------------------------------------------------
 */

export interface TemplateEditorProps {
  /** Seed content, normally handed over from a list row via router state. */
  initial?: Partial<TemplateSummary>;
  /** Edit the seed IN PLACE (same version) instead of proposing a new version of it. Normally
   *  handed over from the template list's "Edit" row action via router state; exposed as a prop
   *  too, same as `initial`, so tests can set it directly. */
  editInPlace?: boolean;
  /** Locks the channel selector because the CALLER already fixed it — the Template List's "New
   *  template" button (2026-08-06) seeds `initial.channel` from whichever tab (Email/SMS) it was
   *  clicked on and sets this, since there is nothing left to choose. Deliberately separate from
   *  `initial.channel` alone, which by itself still leaves the field editable — a caller may seed
   *  a starting value without committing to it; see "leaves the channel editable when authoring a
   *  new template" in the test file. */
  channelLocked?: boolean;
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  initial: injectedInitial,
  editInPlace: injectedEditInPlace,
  channelLocked: injectedChannelLocked,
}) => {
  const { accessToken } = useOidcAccessToken();
  const navigate = useNavigate();
  const location = useLocation();
  const routerState = location.state as
    | { template?: Partial<TemplateSummary>; editInPlace?: boolean; channelLocked?: boolean }
    | null;
  const seed = injectedInitial ?? routerState?.template;
  const isEditInPlace = injectedEditInPlace ?? routerState?.editInPlace ?? false;
  const isChannelLocked = injectedChannelLocked ?? routerState?.channelLocked ?? false;

  /**
   * The template family this save will version, or null for a brand-new template. Deliberately
   * left null when editing in place — that save goes through `editTemplate` (same version), not
   * `createTemplateAsProvider`'s revision path, so this must not also read as "propose a new
   * version" for the exact same seed.
   *
   * STATE, not `seed?.templateMasterUid` read at the point of save — that was the T57
   * frontend defect. "Author another" below resets the fields but cannot reset a value read
   * straight off the seed, so the next save carried the SAME uid and the fixed backend
   * dutifully minted yet another version of the family just edited. Under the OLD backend
   * that was invisible (the uid was discarded); the T50 fix is what turned it into a real
   * bug, which is why fixing the server half alone was not enough.
   */
  const [revisionOf] = useState<string | null>(
    isEditInPlace ? null : (seed?.templateMasterUid ?? null),
  );
  /** The exact (uid, version) being edited in place — captured once at mount, same as
   *  `revisionOf`; there is no "Author another" path out of edit-in-place mode to reset it from. */
  const [editTarget] = useState<{ uid: string; version: number } | null>(
    isEditInPlace && seed?.templateMasterUid != null && seed?.version != null
      ? { uid: seed.templateMasterUid, version: seed.version }
      : null,
  );
  const [channel, setChannel] = useState<number>(seed?.channel ?? CHANNEL_SMS);
  const [name, setName] = useState(seed?.name ?? '');
  const [subject, setSubject] = useState(seed?.subject ?? '');
  const [body, setBody] = useState(seed?.body ?? '');
  // Both optional — neither participates in isInvalid/canSave below. '' means "no category".
  const [category, setCategory] = useState<string>(seed?.templateCategory ?? '');
  const [isDefaultTemplate, setIsDefaultTemplate] = useState<boolean>(seed?.isDefault ?? false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ message: string; httpStatus: number | null } | null>(null);

  // Pending category/default save, held while the "replace existing default?" dialog is open.
  const [pendingSave, setPendingSave] = useState<{
    result: TemplateSummary;
    category: string;
    categoryChanged: boolean;
  } | null>(null);
  const [duplicateHolderName, setDuplicateHolderName] = useState<string | null>(null);

  const [categoryOptions, setCategoryOptions] = useState<TemplateCategoryOption[]>([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const result = await fetchTemplateCategories(accessToken);
      if (alive && result.kind === 'ok') {
        setCategoryOptions(result.data);
      }
    })();
    return () => { alive = false; };
  }, [accessToken]);

  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  /** Inserts at the caret, or appends when the field has never been focused. */
  const insertToken = useCallback(
    (placeholder: string) => {
      const el = bodyRef.current;
      if (!el || el.selectionStart == null) {
        setBody((prev) => (prev === '' ? placeholder : `${prev}${placeholder}`));
        return;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd ?? start;
      setBody((prev) => `${prev.slice(0, start)}${placeholder}${prev.slice(end)}`);
      // Restore the caret after React re-renders the controlled value. Guarded because
      // caret restoration is a nicety — if rAF is unavailable the insert must still work.
      const restoreCaret = () => {
        el.focus();
        const caret = start + placeholder.length;
        el.setSelectionRange(caret, caret);
      };
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(restoreCaret);
      }
    },
    [],
  );

  const unknownTokens = extractTokens(body).filter((t) => !isKnownToken(t));
  const isRevision = !isEditInPlace && revisionOf != null;
  const isEmail = channel === CHANNEL_EMAIL;
  const nameMissing = name.trim() === '';
  const bodyMissing = body.trim() === '';
  const subjectMissing = isEmail && subject.trim() === '';
  const hasUnknownTokens = unknownTokens.length > 0;

  /**
   * T58 — `hasUnknownTokens` is a HARD blocker, not advisory. See the token note in the
   * file header: no provider-side server validation exists behind this, so letting the save
   * through publishes an unresolvable token to every tenant. `canSave` below consults the raw
   * flag directly, so the Save button is genuinely disabled from the first render regardless
   * of whether `blockedReason` has anything to show yet.
   */
  const isInvalid = nameMissing || bodyMissing || subjectMissing || hasUnknownTokens;
  const canSave = !isInvalid && !busy;

  /**
   * The reason SHOWN to the user when the Save button is disabled. Required-field messaging
   * was removed entirely (2026-08-06, not merely deferred — see file header): a disabled
   * button is itself the signal, and the missing-field case needs no further explanation. The
   * unknown-token message stays — that is live feedback on content the user actually typed,
   * not an incomplete-form artifact, and there is no other server-side check behind it (T58).
   */
  const blockedReason = hasUnknownTokens
    ? `${isEditInPlace ? 'Cannot save' : 'Cannot publish'}: ${unknownTokens.map((t) => `{{${t}}}`).join(', ')} ${
        unknownTokens.length === 1 ? 'is not a token that resolves' : 'are not tokens that resolve'
      } at send time. Remove or replace ${unknownTokens.length === 1 ? 'it' : 'them'} first.`
    : null;

  // Land straight on the template list (at the tab that matches this template's channel),
  // carrying the per-path confirmation as a dismissible banner there — there is no separate
  // "Changes saved" screen to stop on any more (user request 2026-08-07). The three messages
  // stay genuinely different: a revision goes live for every tenant, the other two do not.
  const finishSave = (savedTemplate: TemplateSummary) => {
    const savedNotice = {
      title: isEditInPlace
        ? 'Changes saved'
        : isRevision
          ? 'New version saved and live'
          : 'Saved as draft',
      message: isEditInPlace
        ? `“${savedTemplate.name}” v${savedTemplate.version} was updated — still ${statusLabel(
            savedTemplate.status,
          )}, same version, not live for any tenant. Publish it from the template list when you’re ready.`
        : isRevision
          ? `“${savedTemplate.name}” v${savedTemplate.version} is published and live for every tenant now. It replaced the version that was live, which is now marked superseded. Provider edits do not go through review.`
          : `“${savedTemplate.name}” v${savedTemplate.version} was saved as a draft — not live for any tenant yet. Publish it from the template list when you’re ready.`,
    };
    navigate('/communications/templates', {
      state: {
        tab: savedTemplate.channel === CHANNEL_EMAIL ? 'email' : 'sms',
        savedNotice,
      },
    });
  };

  // `applyDefault` of `undefined` leaves the row's default flag untouched (used on Cancel from the
  // duplicate-default dialog — the category may still save, the default does not). The content
  // save (name/subject/body) already succeeded before this runs; a failure HERE must not be
  // reported as success or navigate away — the caller re-lands on the editor with the real
  // template list one refresh away from showing whether it actually took.
  const applyCategoryAndFinish = async (
    result: TemplateSummary,
    categoryToSave: string,
    categoryChanged: boolean,
    applyDefault: boolean | undefined,
  ): Promise<boolean> => {
    if (categoryChanged || applyDefault !== undefined) {
      const patchResult = await setTemplateCategory(
        result.templateMasterUid,
        result.version,
        categoryToSave === '' ? null : categoryToSave,
        accessToken,
        applyDefault,
      );
      if (patchResult.kind !== 'ok') {
        setFailure({
          message:
            patchResult.kind === 'rejected' || patchResult.kind === 'notFound'
              ? patchResult.message
              : 'The template itself saved, but its category/default could not be saved — '
                + 'reopen it from the template list and try again.',
          httpStatus: 'status' in patchResult ? patchResult.status : null,
        });
        return false;
      }
    }
    finishSave(result);
    return true;
  };

  const cancelDuplicateDefault = async () => {
    if (!pendingSave) return;
    const { result, category: categoryToSave, categoryChanged } = pendingSave;
    setDuplicateHolderName(null);
    setPendingSave(null);
    setBusy(true);
    await applyCategoryAndFinish(result, categoryToSave, categoryChanged, undefined);
    setBusy(false);
  };

  const confirmReplaceDefault = async () => {
    if (!pendingSave) return;
    const { result, category: categoryToSave, categoryChanged } = pendingSave;
    setDuplicateHolderName(null);
    setPendingSave(null);
    setBusy(true);
    await applyCategoryAndFinish(result, categoryToSave, categoryChanged, true);
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    setFailure(null);
    const result =
      isEditInPlace && editTarget
        ? await editTemplate(
            editTarget.uid,
            editTarget.version,
            { name: name.trim(), subject: isEmail ? subject.trim() : null, body },
            accessToken,
          )
        : await createTemplateAsProvider(
            {
              templateMasterUid: revisionOf,
              channel,
              name: name.trim(),
              subject: isEmail ? subject.trim() : null,
              body,
            },
            accessToken,
          );

    if (result.kind === 'ok') {
      // A create/revision always mints a BRAND-NEW row (templateCategory null, isDefault false),
      // regardless of what the seed (the family's previous version) carried — so the "did this
      // change?" comparison must use that blank baseline, not the seed, or an unchanged-looking
      // category/default is silently never written to the new row. Edit-in-place mutates the SAME
      // row, which already holds the seed's values untouched, so the seed IS the right baseline.
      const baselineCategory = isEditInPlace ? (seed?.templateCategory ?? '') : '';
      const baselineIsDefault = isEditInPlace ? (seed?.isDefault ?? false) : false;
      const categoryChanged = category !== baselineCategory;
      const defaultToggled = isDefaultTemplate !== baselineIsDefault;
      // Must EXPLICITLY (re)send true whenever the box ends up checked and EITHER it changed OR
      // the category changed — moving an already-default template to a different category still
      // needs the channel+category swap to run for the NEW category, not only when the checkbox
      // itself flips (an unchanged-looking checkbox with a changed category was otherwise a silent
      // unique-index violation: the row kept is_default=true while its category moved under it).
      const applyDefault = isDefaultTemplate
        ? (defaultToggled || categoryChanged ? true : undefined)
        : (defaultToggled ? false : undefined);

      // The row this save is SUPERSEDING (a create/revision's seed) never gets touched by the
      // content save above, and a fresh version row never inherits is_default — so explicitly
      // turning the checkbox OFF here must demote the seed's own row, or that now-superseded row
      // stays the recorded default with nothing in the UI still pointing at it.
      if (!isEditInPlace && seed?.isDefault === true && !isDefaultTemplate
          && seed?.templateMasterUid && seed?.version != null) {
        await setTemplateCategory(
          seed.templateMasterUid, seed.version, seed.templateCategory ?? null, accessToken, false,
        );
      }

      if (applyDefault === true) {
        const myFamilyUid = isEditInPlace ? editTarget?.uid : (revisionOf ?? result.data.templateMasterUid);
        const holderRes = await fetchDefaultHolder(result.data.channel, category, accessToken);
        if (holderRes.kind === 'ok' && holderRes.data.templateMasterUid !== myFamilyUid) {
          // Another family already holds the default — pause and let the user decide rather than
          // silently replacing it.
          setPendingSave({ result: result.data, category, categoryChanged });
          setDuplicateHolderName(holderRes.data.name);
          setBusy(false);
          return;
        }
        if (holderRes.kind !== 'ok' && holderRes.kind !== 'notFound') {
          // Fail CLOSED: an unreachable/errored check must never let a default swap through
          // unverified — that is exactly the silent-replace outcome this check exists to prevent.
          setFailure({
            message: 'Could not check whether another template already holds this default. '
              + 'The template itself was saved — try again to set it as the default.',
            httpStatus: null,
          });
          setBusy(false);
          return;
        }
      }
      await applyCategoryAndFinish(result.data, category, categoryChanged, applyDefault);
      setBusy(false);
      return;
    }
    setBusy(false);
    if (result.kind === 'forbidden') {
      setFailure({ message: 'Refused — authoring a template needs provider-admin permission.', httpStatus: 403 });
    } else if (result.kind === 'unauthorized') {
      setFailure({ message: 'Your session is not valid. Sign in again.', httpStatus: 401 });
    } else if (result.kind === 'notFound') {
      // Don't echo the server's raw "master <uuid> vN not found" — a hash the author cannot act on,
      // and the ErrorState "not an empty list" framing made no sense here either (2026-08-11).
      setFailure({
        message:
          'This template could not be found — it may have been changed, published, or removed since '
          + 'the list loaded. Go back to the template list, refresh, and open it again.',
        httpStatus: 404,
      });
    } else if (result.kind === 'rejected') {
      setFailure({ message: result.message, httpStatus: result.status });
    } else {
      setFailure({ message: result.message, httpStatus: result.status });
    }
  };

  return (
    <Box sx={{ p: 3 }} data-testid="template-editor">
      <Typography variant="h5" gutterBottom>
        {isEditInPlace
          ? 'Edit template'
          : isRevision
            ? 'New version of an existing template'
            : 'New template'}
      </Typography>

      {/*
        2026-08-06: both of this screen's explanatory banners were removed at the user's
        request, in three steps over the same day — first the brand-new-template branch, then
        the edit-in-place branch, then (this last step) the two that were left for a revision
        ("saving publishes immediately", "this saves a new VERSION"). Nothing renders here in
        any of the three modes now. The consequences those banners stated are still real — a
        revision still goes live immediately, still versions the same family rather than
        creating a second template — they are just no longer narrated on this screen; the Save
        button's own label (see below: "Save new version — publishes to all tenants") is what
        is left to carry that.
      */}

      {failure && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="state-error">
          {failure.message}
          {failure.httpStatus != null && ` (HTTP ${failure.httpStatus})`}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/*
                Locked on the revision path: proposeChange has no channel parameter and
                inherits the live version's channel, so an edit here would be discarded
                silently (or 400 on SMS -> Email). See the file header. An editable control
                the server ignores is worse than no control. Locked on the edit-in-place path
                too: channel is immutable once a template family exists (editAsProvider never
                accepts one), same underlying rule, different reason to state it. Locked on a
                brand-new template too when the caller already fixed it (2026-08-06): the
                Template List's "New template" button is only ever reached from the Email or SMS
                tab, so the channel is already decided by the time this screen opens — see
                `channelLocked`.
              */}
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 200, flex: '1 1 200px' }}
                    disabled={isRevision || isEditInPlace || isChannelLocked}
                  >
                    <InputLabel id="channel-label">Channel</InputLabel>
                    <Select
                      labelId="channel-label"
                      label="Channel"
                      value={channel}
                      onChange={(e) => setChannel(Number(e.target.value))}
                      inputProps={{ 'data-testid': 'channel-select' }}
                    >
                      <MenuItem value={CHANNEL_SMS}>{channelLabel(CHANNEL_SMS)}</MenuItem>
                      <MenuItem value={CHANNEL_EMAIL}>{channelLabel(CHANNEL_EMAIL)}</MenuItem>
                    </Select>
                  </FormControl>
                  {/* 2026-08-11: the three channel-locked helper captions (revision / edit-in-place /
                      channel-locked) removed at the user's request — the Channel Select is already
                      disabled in each of those cases, which conveys the same thing without the note. */}

                  {/* Optional — not part of nameMissing/bodyMissing/subjectMissing, so Save stays enabled unset. */}
                  <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }}>
                    <InputLabel id="template-category-label">Category</InputLabel>
                    <Select
                      labelId="template-category-label"
                      label="Category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value as string)}
                      inputProps={{ 'data-testid': 'template-category-select' }}
                    >
                      <MenuItem value="">— None —</MenuItem>
                      {categoryOptions.map((option) => (
                        <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Checkbox
                      checked={isDefaultTemplate}
                      onChange={(e) => setIsDefaultTemplate(e.target.checked)}
                      data-testid="template-is-default-checkbox"
                    />
                  }
                  label="Default template for this channel + category"
                />
              </Stack>

              <Divider />

              <TextField
                fullWidth
                size="small"
                label="Template name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                helperText={`${name.length}/200`}
                inputProps={{ maxLength: 200, 'data-testid': 'name-input' }}
              />

              {isEmail && (
                <TextField
                  fullWidth
                  size="small"
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  helperText={`${subject.length}/300`}
                  inputProps={{ maxLength: 300, 'data-testid': 'subject-input' }}
                />
              )}
              {!isEmail && (
                <Typography variant="caption" color="text.secondary" data-testid="sms-no-subject">
                  SMS templates carry no subject — the database rejects one.
                </Typography>
              )}

              {isEmail ? (
                // Email bodies are HTML — edit on the left, live rendered preview on the right.
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'stretch',
                    flexDirection: { xs: 'column', md: 'row' },
                    height: { md: 520 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Body (HTML)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      inputRef={bodyRef}
                      inputProps={{ 'data-testid': 'body-input' }}
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                        '& textarea': {
                          height: '100% !important',
                          overflow: 'auto !important',
                          fontFamily: 'monospace',
                          fontSize: 13,
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Live preview
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <HtmlPreview html={body} testId="editor-html-preview" fill />
                    </Box>
                  </Box>
                </Box>
              ) : (
                // SMS is plain text — same two-pane shape as email so both channels read alike:
                // edit on the left, a live preview of the message exactly as it will send on the right.
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'stretch',
                    flexDirection: { xs: 'column', md: 'row' },
                    height: { md: 520 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Message
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      inputRef={bodyRef}
                      inputProps={{ 'data-testid': 'body-input' }}
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                        '& textarea': { height: '100% !important', overflow: 'auto !important' },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Live preview
                    </Typography>
                    <Box
                      data-testid="editor-sms-preview"
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.default',
                        p: 2,
                        fontSize: 14,
                      }}
                    >
                      {body || (
                        <Typography variant="body2" color="text.disabled">
                          Your message will appear here as it will be sent.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {hasUnknownTokens && (
                <Alert severity="error" data-testid="editor-unknown-tokens">
                  <AlertTitle>Saving is blocked — unknown token(s) in the body</AlertTitle>
                  {unknownTokens.map((t) => `{{${t}}}`).join(', ')} — nothing populates these at send
                  time, so they would render as blank text for every tenant. Use the picker on the
                  right; it lists every token that actually resolves.
                </Alert>
              )}

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  disabled={!canSave}
                  onClick={save}
                  data-testid="save-button"
                >
                  {isEditInPlace
                    ? busy
                      ? 'Saving…'
                      : 'Save changes'
                    : isRevision
                      ? busy
                        ? 'Publishing…'
                        : 'Save new version — publishes to all tenants'
                      : busy
                        ? 'Saving…'
                        : 'Save as draft'}
                </Button>
                <Button variant="text" onClick={() => navigate('/communications/templates')}>
                  Cancel
                </Button>
                {hasUnknownTokens && (
                  <Chip size="small" color="error" label="Body has unresolvable tokens" />
                )}
              </Stack>

              {/*
                A disabled button with no stated reason is a support ticket. This says which
                condition is holding the save, in the same order canSave evaluates them.
              */}
              {blockedReason && !busy && (
                <Typography variant="caption" color="error" data-testid="save-blocked-reason">
                  {blockedReason}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <TokenPicker onInsert={insertToken} disabled={busy} templateCategory={category} />
        </Grid>
      </Grid>

      <Dialog
        open={duplicateHolderName !== null}
        onClose={cancelDuplicateDefault}
        data-testid="duplicate-default-dialog"
      >
        <DialogTitle>Default template already exists</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A default template is already configured for this channel and category. It is
            currently set to &quot;{duplicateHolderName}&quot;. Do you want to replace it with
            this template as the default?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDuplicateDefault} disabled={busy} data-testid="duplicate-default-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={confirmReplaceDefault}
            disabled={busy}
            variant="contained"
            data-testid="duplicate-default-confirm-button"
          >
            Continue / Replace Default
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateEditor;
